import React from "react"
import { Box, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import { AppBulkEditAction, AppDialogCancel } from "@components"
import IrisMark, { IRIS } from "@components/brand/IrisMark"


const HINT_TONE = {
	error: { color: dsColorNew.dangerRed, icon: 'triangle-exclamation' },
	ok: { color: dsColorNew.textMuted, icon: 'check', iconColor: dsColorNew.successText },
	dirty: { color: dsColorNew.textMuted },
	muted: { color: dsColorNew.textMuted },
}

/**
 * The fixed foot of a Settings tab with a draft: the hint on the left (red on error), *Discard* and
 * *Save* on the right (UX D7 of CU-869fknfmk, shared with the Iris tab of CU-869fkmt6r). Sticks to the
 * bottom of the viewport while the tab scrolls. `variant="iris"`: *Save* carries Iris's mark on the
 * blue → cyan gradient of her launcher while it is enabled (UX D7 of CU-869fkmt6r).
 */
export default function SettingsStickyFooter({ hint, onDiscard, discardDisabled, onSave, saveDisabled, saveLabel = 'Save changes', saveIcon = 'floppy-disk', saving = false, variant }) {
	const tone = HINT_TONE[hint?.tone] || HINT_TONE.muted
	const iris = variant === 'iris'

	return <Box sx={{
		position: 'sticky', bottom: 0, zIndex: 2, minHeight: 61,
		display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.25,
		border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '14px 14px 0 0',
		// opaque: long lists scroll under it (QA CU-869fknfmk, plan § 4.5)
		backgroundColor: dsColorNew.white,
	}}>
		<Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, mr: 'auto', fontSize: 13, color: tone.color }}>
			{hint?.tone === 'dirty' && !!hint.count && <Box component="span" sx={{
				px: 1.25, py: 0.4, borderRadius: '999px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
				backgroundColor: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber, border: `1px solid ${dsColorNew.noteAmberBorder}`,
			}}>{hint.count} change{hint.count > 1 ? 's' : ''}</Box>}
			{!!tone.icon && <IconSeeker icon={tone.icon} sx={{ fontSize: 13, color: tone.iconColor || tone.color }}/>}
			<Typography component="span" sx={{ fontSize: 13, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hint?.text}</Typography>
		</Box>
		<AppDialogCancel disabled={discardDisabled} onClick={onDiscard}>Discard</AppDialogCancel>
		<AppBulkEditAction
			sx={{
				minWidth: 140,
				...(iris && { '&:not(.Mui-disabled)': { background: `linear-gradient(135deg, ${dsColorNew.webBlue500}, ${dsColorNew.cyan})` } }),
			}}
			disabled={saveDisabled}
			loading={saving}
			startIcon={iris ? <IrisMark size={IRIS.inline}/> : <IconSeeker icon={saveIcon}/>}
			onClick={onSave}
		>
			{saveLabel}
		</AppBulkEditAction>
	</Box>
}
